import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { GOV_TYPE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientAuthApi, type PatientSessionInfo } from '../api';
import { clearToken } from '../../../api/patientSession';

/**
 * Real device sessions: every login records a session (by token jti) on the
 * backend, and revoking one here genuinely kills that token — the next
 * request from that device gets a 401. Nothing on this page is simulated.
 */
export default function PatientSecuritySessions() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<PatientSessionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyJti, setBusyJti] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError('');
    patientAuthApi.getSessions()
      .then(setSessions)
      .catch(() => setLoadError('Sessions could not be loaded — check your connection and try again.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmt = (d: string) => {
    try { return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }); }
    catch { return d; }
  };

  const handleRevoke = async (s: PatientSessionInfo) => {
    const isCurrent = s.current;
    if (!confirm(isCurrent
      ? 'Sign out THIS device? You will be returned to the login screen.'
      : `Sign out "${s.deviceLabel}"? That device will need to log in again.`)) return;
    setBusyJti(s.jti);
    setNotice('');
    try {
      await patientAuthApi.revokeSession(s.jti);
      if (isCurrent) {
        clearToken();
        navigate('/patient/entry', { replace: true });
        return;
      }
      setNotice(`"${s.deviceLabel}" has been signed out.`);
      load();
    } catch {
      setLoadError('Sign-out failed — the server is unreachable. The session is STILL active; please retry.');
    } finally {
      setBusyJti(null);
    }
  };

  const handleRevokeOthers = async () => {
    if (!confirm('Sign out every other device? Only this session will stay logged in.')) return;
    setNotice('');
    try {
      const res = await patientAuthApi.revokeOtherSessions();
      setNotice(res.revoked > 0
        ? `${res.revoked} other session${res.revoked !== 1 ? 's' : ''} signed out.`
        : 'No other active sessions were found.');
      load();
    } catch {
      setLoadError('Could not sign out other devices — the server is unreachable. Please retry.');
    }
  };

  const others = sessions.filter(s => !s.current).length;

  return (
    <div style={{ ...govPageContainer({ maxWidth: 900 }), display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={GOV_TYPE.pageTitle}>Active Sessions & Devices</div>
        <div style={{ fontSize: 11, color: T.textSec, fontFamily: T.font, marginTop: 2 }}>
          Every device signed in to your NeoHealthCard account. Signing one out immediately invalidates its access.
        </div>
      </div>

      {notice && (
        <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 10, padding: '10px 14px', fontSize: 11, fontWeight: 600, color: '#065F46', fontFamily: T.font }}>{notice}</div>
      )}
      {loadError && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10, padding: '10px 14px', fontSize: 11, fontWeight: 600, color: '#B91C1C', fontFamily: T.font }}>{loadError}</div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 30, fontSize: 12, color: T.textSec, fontFamily: T.font }}>Loading sessions…</div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sessions.map(s => (
              <div key={s.jti} style={{
                background: T.white, border: `1px solid ${s.current ? T.teal : T.border}`, borderRadius: 14,
                padding: 14, boxShadow: T.shadow, display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${s.current ? T.teal : T.navy}12`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={s.current ? T.teal : T.navy} strokeWidth="2" strokeLinecap="round"><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" /></svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: T.font }}>{s.deviceLabel}</span>
                    {s.current && (
                      <span style={{ fontSize: 8, fontWeight: 700, color: '#065F46', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 4, padding: '1px 6px', fontFamily: T.font }}>THIS DEVICE</span>
                    )}
                  </div>
                  <div style={{ fontSize: 9, color: T.textTer, fontFamily: T.font, marginTop: 2 }}>
                    Signed in {fmt(s.issuedAt)} · {s.authMethod === 'PASSWORD' ? 'Password' : s.authMethod === 'SIGNUP' ? 'Registration' : 'OTP'} · Last active {fmt(s.lastSeenAt)}
                  </div>
                </div>
                <button
                  onClick={() => handleRevoke(s)}
                  disabled={busyJti === s.jti}
                  style={{
                    fontSize: 10, fontWeight: 700, fontFamily: T.font, color: '#B91C1C',
                    background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8,
                    padding: '6px 12px', cursor: 'pointer', opacity: busyJti === s.jti ? 0.6 : 1, flexShrink: 0,
                  }}
                >
                  {busyJti === s.jti ? 'Signing out…' : 'Sign out'}
                </button>
              </div>
            ))}
            {sessions.length === 0 && !loadError && (
              <div style={{ textAlign: 'center', padding: 24, fontSize: 11, color: T.textSec, fontFamily: T.font, background: T.ice, borderRadius: 12 }}>
                No tracked sessions yet — sessions are recorded from your next login onward.
              </div>
            )}
          </div>

          {others > 0 && (
            <button
              onClick={handleRevokeOthers}
              style={{ width: '100%', padding: '12px 0', borderRadius: 12, background: '#B91C1C', color: '#FFF', fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer', fontFamily: T.font }}
            >
              Sign out all other devices ({others})
            </button>
          )}
        </>
      )}

      <button
        onClick={() => navigate('/patient/security')}
        style={{ width: '100%', padding: '10px 0', borderRadius: 10, background: 'transparent', color: T.textSec, fontWeight: 600, fontSize: 12, border: `1px solid ${T.border}`, cursor: 'pointer', fontFamily: T.font }}
      >
        Back to Security & Privacy
      </button>
    </div>
  );
}
